import { Box, Button, CircularProgress, Grid, IconButton, Snackbar, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import KeyboardVoiceIcon from '@mui/icons-material/KeyboardVoice';
import useSpeechRecognition from '../../../Hooks/useSpeechRecognition';
import { useGlobalState } from '../../../GlobalStateContext';
import MuiAlert from '@mui/material/Alert';

const MAX_CHARS = 20000;

interface DemoInputComponentProps {
	setInput: (value: string) => void;
	setShowChat: (value: boolean) => void;
	input: string;
	APIError: boolean;
	inputPlaceholder: string;
}

const DemoInputComponent: React.FC<DemoInputComponentProps> = ({ setInput, input, setShowChat, APIError, inputPlaceholder }) => {
	const [showInputErrorMessage, setShowInputErrorMessage] = useState(false);
	const [inputErrorMessage, setInputErrorMessage] = useState('');

	const { startListening, stopListening, transcript, resetTranscript } =
    useSpeechRecognition();

	
	const [isLoading, setIsLoading] = useState(false);
	const [typingStatus, setTypingStatus] = useState('Initializing');
	const [isLoadingSend, setIsLoadingSend] = useState(false);
	const [clicked, setClicked] = useState(false);

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.value;
		const isPaste = event.nativeEvent && (event.nativeEvent as InputEvent).inputType === 'insertFromPaste';
		if (newValue.length <= MAX_CHARS) {
			setShowInputErrorMessage(false);
			setInput(newValue);
		} else if (!isPaste) {
			setInputErrorMessage(`Tu mensaje supera el límite de ${MAX_CHARS} caracteres. ¿Podrías intentarlo de nuevo?`);
			setShowInputErrorMessage(true);
		}
	};

	const handlePaste = (event: React.ClipboardEvent) => {
		const pastedText = event.clipboardData.getData('text');
		if (pastedText.length + input.length > MAX_CHARS) {
			event.preventDefault();
			setInputErrorMessage('El texto que estás pegando es muy largo. ¡Intenta con uno más corto!');
			setShowInputErrorMessage(true);
		} else {
			setShowInputErrorMessage(false);
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			handleSend(input);
		}
	};

	const toggleMicrophone = () => {
		if (clicked) {
			stopListening();
		} else {
			resetTranscript();
			startListening();
		}
		setClicked(!clicked);
	};

	const handleSend = (text: string) => {
		setShowChat(true);
		setTimeout(() => {
			setInput(text);
		}, 100);
	};

	useEffect(() => {
		if (transcript.length) {
			if (transcript.length > MAX_CHARS) {
				setInputErrorMessage(`El mensaje de voz no debe superar los ${MAX_CHARS} caracteres.`);
				setShowInputErrorMessage(true);
				setInput(transcript.substring(0, MAX_CHARS));
				return;
			}
			setInput(transcript);
			setShowChat(true);
		}
	}, [transcript]);

	useEffect(() => {
		if (APIError) {
			setShowInputErrorMessage(true);
			setInputErrorMessage('Servicio no disponible');
		} else {
			setShowInputErrorMessage(false);
			setInputErrorMessage('');
		}
	}, [APIError]);

	return (
		<Box>
			<Grid
				container
				spacing={2}
				style={{ paddingBottom: '16px', justifyContent: 'space-between' }}
			>
				<Grid item style={{ flexBasis: 'calc(100% - 64px)' }}>
					<TextField
						size="small"
						fullWidth
						placeholder={inputPlaceholder}
						variant="outlined"
						value={input}
						onChange={handleInputChange}
						onPaste={handlePaste}
						disabled={isLoading || typingStatus == 'Typing' || APIError}
						autoComplete={'off'}
						onKeyDown={handleKeyDown}
						inputProps={{
							maxLength: MAX_CHARS + 1
						}}
						sx={{
							'& .MuiInputBase-root': {
								borderRadius: '40px',
								'&:hover': {
									'& .MuiOutlinedInput-notchedOutline': {
										borderColor: 'rgba(127, 17, 224, 1)'
									}
								},
							},
							'& .MuiOutlinedInput-notchedOutline': {
								borderColor: 'rgba(127, 17, 224, 0.6)',
							},
						}}
						InputProps={{
							endAdornment: (
								<Button
									disabled={
										isLoading ||
                    isLoadingSend ||
                    typingStatus == 'Typing' ||
										APIError
									}
									sx={{
										backgroundColor: 'rgba(116, 17, 154, 0.9)',
										color: 'white',
										width: '2rem',
										height: '2rem',
										borderRadius: '50%',
										minWidth: 'unset',
										margin: '11px 0px 11px 11px',
										position: 'relative',
										'&:hover': {
											backgroundColor: 'rgba(116, 17, 154, 1)',
											color: 'white',
										},
										'&:disabled': {
											backgroundColor: '#999',
											color: 'white',
										}
									}}
									onClick={() => handleSend(input)}
								>
									{isLoadingSend ||
                    typingStatus == 'Typing' ? (
											<CircularProgress
												color="inherit"
												size={24}
												style={{
													position: 'absolute',
												}}
											/>
										) : (
											<ArrowUpwardIcon sx={{ transform: 'rotate(90deg)'}} />
										)}
								</Button>
							),
						}}
					/>
					{showInputErrorMessage && <Typography sx={{fontSize: '12px', color: 'red', textAlign: 'center', margin: '8px 0 0 0' }}>{inputErrorMessage}</Typography>}
				</Grid>
				<Grid item style={{ flexBasis: '64px' }}
				>
					<IconButton
						color={clicked ? 'error' : 'default'}
						size='large'
						style={{
							position: 'absolute',
						}}
						onClick={toggleMicrophone}
						disabled={APIError}
					>
						<KeyboardVoiceIcon />
					</IconButton>
				</Grid>
			</Grid>
		</Box>
	);
};

export default DemoInputComponent;